import './products.css';
import { Button, Checkbox, Input, Popconfirm, Select, Tooltip, Tree, TreeSelect, message } from 'antd';
import {
    AppstoreOutlined, CheckSquareOutlined, DeleteOutlined, EditOutlined, FolderAddOutlined, InboxOutlined,
    MinusSquareOutlined, PlusOutlined, QuestionCircleOutlined,
} from '@ant-design/icons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { serverFetch } from '@server';
import { showError, fmtMoney, getStored, setStored } from '@extensions/utils';
import { useDicts } from '@extensions/dicts';
import CardList from '@controls/card-list/card-list';
import { buildGroupTree, countByGroup, descendantIds, toSelectTree } from './product-groups';
import Product, { NO_GROUP } from './product';

const ALL = 'all';
const NONE = 'none';
const DRAG_MIME = 'application/x-botva-products';

const hasProducts = (e) => Array.from(e.dataTransfer?.types ?? []).includes(DRAG_MIME);

const SORTS = {
    name: { label: 'По наименованию', cmp: (a, b) => a.name.localeCompare(b.name, 'ru') },
    priceAsc: { label: 'Сначала дешёвые', cmp: (a, b) => Number(a.price) - Number(b.price) },
    priceDesc: { label: 'Сначала дорогие', cmp: (a, b) => Number(b.price) - Number(a.price) },
    newest: { label: 'Сначала новые', cmp: (a, b) => b.id - a.id },
};

// Подсветка найденного фрагмента
const highlight = (text, q) => {
    const i = q && text ? text.toLowerCase().indexOf(q) : -1;
    if (i < 0) return text;
    return <>{text.slice(0, i)}<mark>{text.slice(i, i + q.length)}</mark>{text.slice(i + q.length)}</>;
};

const Products = () => {
    const user = useSelector((s) => s.user);
    const canEdit = user?.role <= 10;
    const { reloadDicts } = useDicts();
    const [products, setProducts] = useState([]);
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selected, setSelected] = useState(() => getStored('productGroup', ALL));
    const [expanded, setExpanded] = useState(() => getStored('productGroupsExpanded', []));
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    useEffect(() => {
        const t = setTimeout(() => setSearch(searchInput), 300);
        return () => clearTimeout(t);
    }, [searchInput]);
    const [sort, setSort] = useState(() => (SORTS[getStored('productSort')] ? getStored('productSort') : 'name'));
    const [editing, setEditing] = useState(null); // товар в Drawer, id = 0 — новый
    const [checked, setChecked] = useState([]);
    const [renaming, setRenaming] = useState(null);
    const [dropTarget, setDropTarget] = useState(null);
    const draggingGroup = useRef(null);
    const renameCancel = useRef(false);
    const lastChecked = useRef(null);

    const load = useCallback(() => {
        setLoading(true);
        Promise.all([serverFetch('product'), serverFetch('productgroup')])
            .then(([p, g]) => { setProducts(p); setGroups(g); })
            .catch(showError)
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { setStored('productGroup', selected); }, [selected]);
    useEffect(() => { setStored('productGroupsExpanded', expanded); }, [expanded]);
    useEffect(() => { setStored('productSort', sort); }, [sort]);

    const groupById = useMemo(() => new Map(groups.map((g) => [g.id, g])), [groups]);
    const tree = useMemo(() => buildGroupTree(groups), [groups]);
    const counts = useMemo(() => countByGroup(tree, products), [tree, products]);
    const groupOptions = useMemo(() => [{ value: NO_GROUP, title: '— Без группы —' }, ...toSelectTree(tree)], [tree]);
    const units = useMemo(() => [...new Set(products.map((p) => p.unit).filter(Boolean))].sort(), [products]);
    const noGroupCount = useMemo(() => products.filter((p) => p.groupId == null || !groupById.has(p.groupId)).length, [products, groupById]);

    useEffect(() => {
        if (typeof selected === 'number' && groups.length && !groupById.has(selected)) setSelected(ALL);
    }, [selected, groups, groupById]);

    const q = search.trim().toLowerCase();
    const rows = useMemo(() => {
        const scope = typeof selected === 'number' ? descendantIds(groups, selected) : null;
        return products.filter((p) => {
            if (selected === NONE && p.groupId != null && groupById.has(p.groupId)) return false;
            if (scope && !scope.has(p.groupId)) return false;
            return !q || p.name.toLowerCase().includes(q) || (p.description ?? '').toLowerCase().includes(q);
        }).sort(SORTS[sort].cmp);
    }, [products, groups, groupById, selected, q, sort]);

    // ===== Товары =====
    const changed = () => { reloadDicts(); };

    const openNew = () => setEditing({
        id: 0, name: '', unit: '', price: 0, description: '',
        groupId: typeof selected === 'number' ? selected : null,
    });

    const onProductSaved = (saved, isNew) => {
        setProducts((list) => (isNew ? [saved, ...list] : list.map((p) => (p.id === saved.id ? { ...p, ...saved } : p))));
        changed();
    };

    const moveProducts = (ids, groupId) => {
        const target = groupId === NO_GROUP ? null : groupId;
        const list = ids.filter((id) => (products.find((p) => p.id === id)?.groupId ?? null) !== target);
        if (!list.length) return;
        serverFetch('product/move', { method: 'POST', bodyData: { ids: list, groupId: target } })
            .then(() => {
                setProducts((ps) => ps.map((p) => (list.includes(p.id) ? { ...p, groupId: target } : p)));
                const name = target == null ? 'без группы' : `в «${groupById.get(target)?.name}»`;
                message.success(`Перенесено ${name}: ${list.length}`);
                changed();
            })
            .catch(showError);
    };

    const deleteProducts = (ids) => {
        Promise.allSettled(ids.map((id) => serverFetch(`product/${id}`, { method: 'DELETE' })))
            .then((results) => {
                const failed = results.filter((r) => r.status === 'rejected');
                const deleted = ids.filter((_, i) => results[i].status === 'fulfilled');
                setProducts((ps) => ps.filter((p) => !deleted.includes(p.id)));
                setChecked((c) => c.filter((id) => !deleted.includes(id)));
                if (deleted.length) changed();
                if (failed.length) showError(failed[0].reason);
                if (deleted.length > 1) message.success(`Удалено товаров: ${deleted.length}`);
                if (deleted.includes(editing?.id)) setEditing(null);
            });
    };

    // ===== Группы =====
    const addGroup = (parentId) => {
        serverFetch('productgroup', { method: 'POST', bodyData: { name: 'Новая группа', parentId } })
            .then((g) => {
                setGroups((gs) => [...gs, g]);
                if (parentId) setExpanded((k) => (k.includes(parentId) ? k : [...k, parentId]));
                setRenaming(g.id);
                changed();
            })
            .catch(showError);
    };

    const updateGroup = (g, patch) => {
        const next = { ...g, ...patch };
        setGroups((gs) => gs.map((x) => (x.id === g.id ? next : x)));
        serverFetch(`productgroup/${g.id}`, { method: 'PUT', bodyData: next })
            .then(changed)
            .catch((e) => { showError(e); load(); });
    };

    const finishRename = (g, value) => {
        setRenaming(null);
        const name = value.trim();
        if (renameCancel.current) {
            renameCancel.current = false;
            return;
        }
        if (name && name !== g.name) updateGroup(g, { name });
    };

    const moveGroup = (g, parentId) => {
        if ((g.parentId ?? null) === parentId) return;
        updateGroup(g, { parentId });
        if (parentId) setExpanded((k) => (k.includes(parentId) ? k : [...k, parentId]));
    };

    const deleteGroup = (g) => {
        serverFetch(`productgroup/${g.id}`, { method: 'DELETE' })
            .then(() => {
                if (selected === g.id) setSelected(g.parentId ?? ALL);
                load();
                changed();
            })
            .catch(showError);
    };

    const onTreeDrop = (info) => {
        const g = groupById.get(info.dragNode.key);
        const target = groupById.get(info.node.key);
        if (!g || !target) return;
        const parentId = info.dropToGap ? (groupById.has(target.parentId) ? target.parentId : null) : target.id;
        moveGroup(g, parentId);
    };

    // Приём перетаскиваемых товаров (из таблицы) и групп (из дерева) на узел
    const dropProps = (key, { productGroup, acceptGroup } = {}) => {
        if (!canEdit) return {};
        const accepts = (e) => (productGroup !== undefined && hasProducts(e)) || (acceptGroup && draggingGroup.current != null);
        return {
            onDragEnter: (e) => { if (accepts(e)) { e.preventDefault(); e.stopPropagation(); } },
            onDragOver: (e) => {
                if (!accepts(e)) return;
                e.preventDefault();
                e.stopPropagation();
                e.dataTransfer.dropEffect = 'move';
                if (dropTarget !== key) setDropTarget(key);
            },
            onDragLeave: (e) => {
                if (!accepts(e) || e.currentTarget.contains(e.relatedTarget)) return;
                e.stopPropagation();
                setDropTarget((t) => (t === key ? null : t));
            },
            onDrop: (e) => {
                if (!accepts(e)) return;
                e.preventDefault();
                e.stopPropagation();
                setDropTarget(null);
                if (hasProducts(e)) {
                    moveProducts(JSON.parse(e.dataTransfer.getData(DRAG_MIME)), productGroup);
                } else {
                    const g = groupById.get(draggingGroup.current);
                    if (g) moveGroup(g, null);
                }
            },
        };
    };

    const renderGroupTitle = (node) => {
        const g = node.group;
        if (renaming === g.id) {
            return (
                <Input
                    size="small"
                    autoFocus
                    defaultValue={g.name}
                    onFocus={(e) => e.target.select()}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => {
                        e.stopPropagation();
                        if (e.key === 'Enter') e.target.blur();
                        if (e.key === 'Escape') { renameCancel.current = true; e.target.blur(); }
                    }}
                    onBlur={(e) => finishRename(g, e.target.value)}
                />
            );
        }
        return (
            <span
                className={`products-node${dropTarget === g.id ? ' products-drop' : ''}`}
                onDoubleClick={canEdit ? () => setRenaming(g.id) : undefined}
                {...dropProps(g.id, { productGroup: g.id })}
            >
                <span className="products-node-name" title={g.name}>{g.name}</span>
                <span className="products-node-count">{counts.get(g.id) || ''}</span>
                {canEdit && (
                    <span className="products-node-actions" onClick={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()}>
                        <Tooltip title="Добавить подгруппу"><FolderAddOutlined onClick={() => addGroup(g.id)} /></Tooltip>
                        <Tooltip title="Переименовать"><EditOutlined onClick={() => setRenaming(g.id)} /></Tooltip>
                        <Popconfirm
                            title="Удалить группу?"
                            description="Товары и подгруппы перейдут в родительскую группу"
                            okText="Да"
                            cancelText="Нет"
                            onConfirm={() => deleteGroup(g)}
                        >
                            <DeleteOutlined />
                        </Popconfirm>
                    </span>
                )}
            </span>
        );
    };

    const specialNode = (key, icon, label, count, opts) => (
        <div
            className={`products-special${selected === key ? ' products-special-selected' : ''}${dropTarget === key ? ' products-drop' : ''}`}
            onClick={() => setSelected(key)}
            {...dropProps(key, opts)}
        >
            {icon}
            <span className="products-node-name">{label}</span>
            <span className="products-node-count">{count || ''}</span>
        </div>
    );

    // ===== Карточки товаров =====
    const rowIds = rows.map((r) => r.id);
    const allChecked = rowIds.length > 0 && rowIds.every((id) => checked.includes(id));

    // Отметка товара: Shift — диапазон от последнего отмеченного
    const check = (id, on, shift) => {
        const from = rowIds.indexOf(lastChecked.current);
        const to = rowIds.indexOf(id);
        const ids = shift && from >= 0 && to >= 0
            ? rowIds.slice(Math.min(from, to), Math.max(from, to) + 1)
            : [id];
        setChecked((c) => (on ? [...new Set([...c, ...ids])] : c.filter((x) => !ids.includes(x))));
        lastChecked.current = id;
    };

    const onCardClick = (r, e) => {
        if (canEdit && (e.shiftKey || e.ctrlKey || e.metaKey)) {
            check(r.id, e.shiftKey ? true : !checked.includes(r.id), e.shiftKey);
        } else {
            setEditing(r);
        }
    };

    const renderTitle = (r) => (
        <>
            {canEdit && (
                <span onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                        checked={checked.includes(r.id)}
                        onChange={(e) => check(r.id, e.target.checked, e.nativeEvent.shiftKey)}
                    />
                </span>
            )}
            <span className="products-card-name" title={r.name}>{highlight(r.name, q)}</span>
            <span className="products-card-desc" title={r.description || undefined}>{highlight(r.description, q)}</span>
            <span className="products-card-money">
                <span className={`products-card-price${Number(r.price) ? '' : ' card-list-muted'}`}>{fmtMoney(r.price)}</span>
                <span className="products-card-unit card-list-muted" title={r.unit || undefined}>{r.unit}</span>
            </span>
        </>
    );

    const cardProps = (r) => ({
        className: [checked.includes(r.id) && 'products-card-checked', editing?.id === r.id && 'products-card-open']
            .filter(Boolean).join(' '),
        draggable: canEdit,
        onDragStart: canEdit ? (e) => {
            const ids = checked.includes(r.id) ? checked : [r.id];
            e.dataTransfer.setData(DRAG_MIME, JSON.stringify(ids));
            e.dataTransfer.effectAllowed = 'move';
        } : undefined,
        onDragEnd: () => setDropTarget(null),
    });


    return (
        <div>
            <div className="products-layout">
                <div className="products-groups">
                    <div className="products-groups-header">
                        <span>Группы</span>
                        {canEdit && (
                            <Tooltip
                                title="Перетащите группу, чтобы вложить её в другую, или карточки товаров — чтобы перенести их в группу. Двойной клик — переименовать."
                            >
                                <QuestionCircleOutlined className="products-groups-help" />
                            </Tooltip>
                        )}
                        {canEdit && (
                            <Tooltip title="Добавить группу">
                                <Button size="small" type="text" icon={<FolderAddOutlined />} onClick={() => addGroup(null)} />
                            </Tooltip>
                        )}
                    </div>
                    {specialNode(ALL, <AppstoreOutlined />, 'Все товары', products.length, { acceptGroup: true })}
                    {specialNode(NONE, <InboxOutlined />, 'Без группы', noGroupCount, { productGroup: null })}
                    <Tree
                        blockNode
                        className="products-tree"
                        treeData={tree}
                        selectedKeys={typeof selected === 'number' ? [selected] : []}
                        onSelect={(_, { node }) => setSelected(node.key)}
                        expandedKeys={expanded}
                        onExpand={setExpanded}
                        titleRender={renderGroupTitle}
                        draggable={canEdit ? { icon: false, nodeDraggable: (n) => n.key !== renaming } : false}
                        onDragStart={({ node }) => { draggingGroup.current = node.key; }}
                        onDragEnd={() => { draggingGroup.current = null; setDropTarget(null); }}
                        onDrop={onTreeDrop}
                    />
                </div>
                <div className="products-list">
                    <div className="page-toolbar">
                        {canEdit && (
                            <Tooltip title="Добавить товар">
                                <Button type="primary" icon={<PlusOutlined />} onClick={openNew} />
                            </Tooltip>
                        )}
                        {canEdit && (
                            <Tooltip
                                title={allChecked
                                    ? 'Снять отметку со всех'
                                    : 'Отметить все в списке. Shift+клик по карточке — диапазон, Ctrl/⌘+клик — один товар'}
                            >
                                <Button
                                    icon={allChecked ? <MinusSquareOutlined /> : <CheckSquareOutlined />}
                                    disabled={!rowIds.length}
                                    onClick={() => setChecked(allChecked
                                        ? checked.filter((id) => !rowIds.includes(id))
                                        : [...new Set([...checked, ...rowIds])])}
                                />
                            </Tooltip>
                        )}
                        <Input
                            allowClear
                            placeholder="Поиск по наименованию и описанию"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            style={{ flex: '1 1 220px' }}
                        />
                        {canEdit && checked.length > 0 && (
                            <>
                                <span className="page-toolbar-info">Выбрано: {checked.length}</span>
                                <TreeSelect
                                    placeholder="Перенести в группу"
                                    style={{ width: 220 }}
                                    value={null}
                                    showSearch
                                    treeDefaultExpandAll
                                    treeNodeFilterProp="title"
                                    popupMatchSelectWidth={false}
                                    treeData={groupOptions}
                                    onChange={(groupId) => moveProducts(checked, groupId)}
                                />
                                <Popconfirm
                                    title={`Удалить выбранные товары (${checked.length})?`}
                                    okText="Да"
                                    cancelText="Нет"
                                    onConfirm={() => deleteProducts(checked)}
                                >
                                    <Button danger icon={<DeleteOutlined />}>Удалить</Button>
                                </Popconfirm>
                            </>
                        )}
                        <Select
                            value={sort}
                            onChange={setSort}
                            style={{ width: 180 }}
                            options={Object.entries(SORTS).map(([value, { label }]) => ({ value, label }))}
                        />
                        <span className="page-toolbar-info card-list-muted">Всего: {rows.length}</span>
                    </div>
                    <CardList
                        items={rows}
                        loading={loading}
                        emptyText={search ? 'Ничего не найдено' : 'Товаров нет'}
                        renderTitle={renderTitle}
                        cardProps={cardProps}
                        onClick={onCardClick}
                        onDelete={canEdit ? (r) => deleteProducts([r.id]) : undefined}
                        deleteTitle="Удалить товар?"
                    />
                </div>
            </div>
            {editing && (
                <Product
                    key={editing.id || 'new'}
                    product={editing}
                    groupOptions={groupOptions}
                    units={units}
                    readOnly={!canEdit}
                    onClose={() => setEditing(null)}
                    onSaved={onProductSaved}
                />
            )}
        </div>
    );
};

export default Products;
